import { Injectable } from '@angular/core'
import { Observable, tap } from 'rxjs'
import {
    ClusteringParametersWithName,
    IntensityParameters,
    Job,
    NeighborhoodEnrichmentParameters,
    RecurrentNeighborhoodsParameters,
    RegistrationParameters,
    SegmentationParameters,
    CustomSegmentationParameters,
    NucleusExpansionParameters,
    PlottingParameters,
    SegmentationModel,
} from '../model/jobs'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../environments/environment'
import { CaseUtilsService } from './case-utils.service'
import { Store } from '@ngrx/store'
import { jobActions } from '../../state/job/job.actions'
import { ToasterService } from './toaster.service'
@Injectable({
    providedIn: 'root',
})
export class JobApiService {
    private readonly baseUrl = environment.apiUrl

    constructor(
        private readonly http: HttpClient,
        private store: Store,
        private readonly caseUtils: CaseUtilsService,
        private readonly toaster: ToasterService
    ) {}

    getJobs(): Observable<Job[]> {
        return this.caseUtils.createObservableWithCaseId<Job[]>(caseId =>
            this.http.get<Job[]>(`${this.baseUrl}/datasets/${caseId}/jobs`)
        )
    }

    getDapiLayers(): Observable<string[]> {
        return this.caseUtils.createObservableWithCaseId<string[]>(caseId =>
            this.http.get<string[]>(`${this.baseUrl}/datasets/${caseId}/dapi`)
        )
    }

    getSegmentationModels(): Observable<SegmentationModel[]> {
        return this.http.get<SegmentationModel[]>(`${this.baseUrl}/models`)
    }

    startSegmentationJob(
        parameters: SegmentationParameters | CustomSegmentationParameters
    ): Observable<number> {
        return this.startJob(parameters)
    }

    startNucleusExpansionJob(parameters: NucleusExpansionParameters): Observable<number> {
        return this.startJob(parameters)
    }

    startRegistrationJob(parameters: RegistrationParameters): Observable<number> {
        return this.startJob(parameters)
    }

    startClusteringJob(parameters: ClusteringParametersWithName): Observable<number> {
        return this.startJob(parameters)
    }

    startIntensityJob(parameters: IntensityParameters): Observable<number> {
        return this.startJob(parameters)
    }

    startNeighborhoodEnrichmentJob(
        parameters: NeighborhoodEnrichmentParameters
    ): Observable<number> {
        return this.startJob(parameters)
    }

    startRecurrentNeighborhoodsJob(
        parameters: RecurrentNeighborhoodsParameters
    ): Observable<number> {
        return this.startJob(parameters)
    }

    startPlottingJob(parameters: PlottingParameters): Observable<number> {
        return this.startJob(parameters)
    }

    checkPlottingJobWarnings(config: any): Observable<number> {
        return this.caseUtils.createObservableWithCaseId<number>(caseId =>
            this.http.put<number>(
                `${this.baseUrl}/datasets/${caseId}/jobs/plotting/warnings`,
                config
            )
        )
    }

    private startJob(config: JobConfiguration): Observable<number> {
        return this.caseUtils.createObservableWithCaseId<number>(caseId =>
            this.http.post<number>(`${this.baseUrl}/datasets/${caseId}/jobs`, config).pipe(
                tap(() => {
                    this.toaster.success('Job started', 'Job started successfully')
                    this.store.dispatch(jobActions.reloadJobs())
                })
            )
        )
    }
}

type JobConfiguration =
    | SegmentationParameters
    | CustomSegmentationParameters
    | NucleusExpansionParameters
    | RegistrationParameters
    | IntensityParameters
    | RecurrentNeighborhoodsParameters
    | NeighborhoodEnrichmentParameters
    | ClusteringParametersWithName
    | PlottingParameters
